
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from '@mui/material';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import theme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import { API_BASE_URL } from '../lib/constants';
import DraftSaveButton from '../components/DraftSaveButton';
import { DRAFT_KEYS } from '../utils/draftUtils';

import { useNotification } from '../contexts/NotificationContext';

const AddFeedback = () => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    uhid_no: '',
    mobile_no: '',
    feedback_date: '',
    feedback_reason: '',
    other_reason: '',
    doctor_name: ''
  });

  const [patients, setPatients] = useState([]);
  const navigate = useNavigate();
  const doctorList = [
    { id: 1, name: "Dr. Alice Smith" },
    { id: 2, name: "Dr. John Doe" },
    { id: 3, name: "Dr. Emily Johnson" },
    { id: 4, name: "Dr. Michael Brown" },
    { id: 5, name: "Dr. Sarah Davis" },
    { id: 6, name: "Dr. David Wilson" },
    { id: 7, name: "Dr. Laura Taylor" },
    { id: 8, name: "Dr. James Anderson" },
    { id: 9, name: "Dr. Olivia Thomas" },
    { id: 10, name: "Dr. Daniel Martinez" }
  ];

  useEffect(() => {
    axios.get(`${API_BASE_URL}/get_all_patients`, { withCredentials: true })
      .then(response => setPatients(response.data))
      .catch(error => {
        console.error('Error fetching patients:', error);
        showNotification('error', 'Failed to load patient list.');
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.uhid_no) { showNotification('error', 'Required Field: Please select a Patient UHID.'); return false; }
    if (!formData.feedback_date) { showNotification('error', 'Required Field: Please select a Feedback Date.'); return false; }
    if (!formData.doctor_name) { showNotification('error', 'Required Field: Please select a Doctor.'); return false; }
    if (!formData.feedback_reason) { showNotification('error', 'Required Field: Please select a Feedback Reason.'); return false; }
    if (formData.feedback_reason === 'Others' && !formData.other_reason) {
      showNotification('error', 'Required Field: Please specify the other reason.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        uhid_no: formData.uhid_no,
        feedback_date: formData.feedback_date,
        feedback_reason: formData.feedback_reason,
        other_reason: formData.feedback_reason === 'Others' ? formData.other_reason : null,
        doctor_name: formData.doctor_name
      };

      await axios.post(`${API_BASE_URL}/add_feedback`, payload, { withCredentials: true });
      showNotification('success', 'Feedback submitted successfully!');
      navigate('/home', { state: { activeMenu: 'Patient Feedback' } });
    } catch (error) {
      console.error('Submission error:', error);
      const msg = error.response?.data?.error || 'Error submitting feedback. Check server connection.';
      showNotification('error', msg);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <button
            onClick={() => navigate('/home', { state: { activeMenu: 'Patient Feedback' } })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            <Typography variant="h4" sx={{ color: '#008080', fontWeight: 600 }}>
              ADD FEEDBACK
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography variant="h6" sx={{ color: '#008080', marginLeft: 1 }}>
                OTMS
              </Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* UHID No */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={<span>UHID No <span style={{ color: 'red' }}>*</span></span>}
                  name="uhid_no"
                  value={formData.uhid_no}
                  onChange={handleChange}
                  fullWidth
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.uhid_no} value={patient.uhid_no}>
                      {patient.uhid_no}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Mobile No */}
              <Grid item xs={12} sm={6}>
                <PhoneInput
                  country={'in'}
                  value={formData.mobile_no}
                  onChange={(value) => setFormData((prev) => ({ ...prev, mobile_no: value }))}
                  inputProps={{
                    name: 'mobile_no',
                    required: true,
                    autoFocus: true,
                  }}
                  containerStyle={{ width: '100%' }}
                  inputStyle={{ width: '100%', height: '56px' }}
                  buttonStyle={{ height: '56px' }}
                />
              </Grid>

              {/* Feedback Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Feedback Date <span style={{ color: 'red' }}>*</span></span>}
                  name="feedback_date"
                  type='date'
                  inputProps={{
                    min: new Date().toISOString().split("T")[0],
                  }}
                  value={formData.feedback_date}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Doctor Name */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="doctor-name-label">
                    <span>Doctor Name <span style={{ color: 'red' }}>*</span></span>
                  </InputLabel>
                  <Select
                    labelId="doctor-name-label"
                    name="doctor_name"
                    value={formData.doctor_name}
                    onChange={handleChange}
                    label={<span>Doctor Name <span style={{ color: 'red' }}>*</span></span>}
                  >
                    {doctorList.map((doc) => (
                      <MenuItem key={doc.id} value={doc.name}>
                        {doc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

              </Grid>

              {/* Feedback Reason */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="feedback-reason-label">
                    <span>Reason for Quitting Surgery <span style={{ color: 'red' }}>*</span></span>
                  </InputLabel>
                  <Select
                    labelId="feedback-reason-label"
                    label={<span>Reason for Quitting Surgery <span style={{ color: 'red' }}>*</span></span>}
                    name="feedback_reason"
                    value={formData.feedback_reason}
                    onChange={handleChange}
                  >
                    <MenuItem value="High Cost">High Cost</MenuItem>
                    <MenuItem value="No Insurance Available">No Insurance Available</MenuItem>
                    <MenuItem value="Average Care">Average Care</MenuItem>
                    <MenuItem value="Fear or Anxiety">Fear or Anxiety</MenuItem>
                    <MenuItem value="Postponement Due to Recovery Concerns">Postponement Due to Recovery Concerns</MenuItem>
                    <MenuItem value="Lack of Support">Lack of Support</MenuItem>
                    <MenuItem value="Alternative Treatments">Alternative Treatments</MenuItem>
                    <MenuItem value="Uncertainty or Lack of Information">Uncertainty or Lack of Information</MenuItem>
                    <MenuItem value="Health-Related Concerns">Health-Related Concerns</MenuItem>
                    <MenuItem value="Scheduling or Personal Issues">Scheduling or Personal Issues</MenuItem>
                    <MenuItem value="Others">Others</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* If 'Others' selected, show extra input */}
              {formData.feedback_reason === 'Others' && (
                <Grid item xs={12}>
                  <TextField
                    label={<span>Please specify <span style={{ color: 'red' }}>*</span></span>}
                    name="other_reason"
                    value={formData.other_reason}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={4}
                  />
                </Grid>
              )}
            </Grid>

            {/* Save Draft Button */}
            {/* <DraftSaveButton
              formKey={DRAFT_KEYS.ADD_PATIENT_FEEDBACK}
              uhidNo={formData.uhid_no || 'default'}
              formData={formData}
              onDraftLoaded={handleDraftLoaded}
            /> */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ marginTop: 3 }}
              fullWidth
            >
              Submit Feedback
            </Button>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default AddFeedback;

